import { http, HttpResponse } from "msw";

import { PricingUnit } from "@/pages/Private/redux/media/media.schema";
import { SceneArtifactState } from "@/pages/Private/redux/media/videoWizard.schema";

import {
	aiModelsMock,
	companyCostsMock,
	exportedWizardMock,
	mediaLibraryMock,
	formatPresetsMock,
	mediaJobsMock,
	musicTrackJobMock,
	musicTracksMock,
	narrationJobMock,
	scenarioCostsMock,
	videoExportJobMock,
	videoWizardMock,
} from "./media.mocks";

interface EstimateBody {
	useCase: string;
	aiModelId?: number;
	quantities?: {
		variants?: number;
		formats?: number;
		scenes?: number;
		seconds?: number;
		tokens?: number;
	};
}

/** Mirrors CostLedgerService.quantityFor so the fixture agrees with the real contract. */
const quantityFor = (unit: PricingUnit, quantities: EstimateBody["quantities"] = {}) => {
	switch (unit) {
		case PricingUnit.PER_IMAGE:
			return (quantities.variants ?? 1) * (quantities.formats ?? 1);
		case PricingUnit.PER_SCENE:
			return quantities.scenes ?? 1;
		case PricingUnit.PER_SECOND:
			return quantities.seconds ?? 0;
		case PricingUnit.PER_1K_TOKENS:
			return (quantities.tokens ?? 0) / 1000;
		default:
			return 1;
	}
};

interface TimelineClipBody {
	storyboardSceneId: number;
	position: number;
	inSeconds: number;
	outSeconds: number;
}

/**
 * How many times the export has been asked for in this session. The first press shows the
 * render in flight, the second the finished video — there is no worker behind the fixture
 * to move it along on its own.
 */
let exportCalls = 0;

export const mediaHandlers = [
	http.get("*/api/media/format-presets", () => HttpResponse.json(formatPresetsMock)),
	http.get("*/api/media/models", ({ request }) => {
		const useCase = new URL(request.url).searchParams.get("useCase");

		return HttpResponse.json(aiModelsMock.filter((model) => !useCase || model.useCase === useCase));
	}),
	/**
	 * The one endpoint two surfaces share: the wizard's picker sees the enabled tracks, a
	 * platform admin asking for `includeDisabled` sees all of them. The fixture applies the
	 * same rule the server does rather than serving one list to both.
	 */
	http.get("*/api/media/music-tracks", ({ request }) => {
		const includeDisabled = new URL(request.url).searchParams.get("includeDisabled") === "true";

		return HttpResponse.json(
			includeDisabled ? musicTracksMock : musicTracksMock.filter((track) => track.enabled !== false)
		);
	}),
	http.post("*/api/media/music-tracks", () =>
		HttpResponse.json(musicTracksMock[0], { status: 201 })
	),
	// 202 with the job: the library row appears when the audio lands, not now.
	http.post("*/api/media/music-tracks/generate", () =>
		HttpResponse.json({ job: musicTrackJobMock }, { status: 202 })
	),
	http.patch("*/api/media/music-tracks/:id", async ({ params, request }) => {
		const body = (await request.json()) as Record<string, unknown>;
		const track =
			musicTracksMock.find((candidate) => candidate.id === Number(params.id)) ?? musicTracksMock[0];

		return HttpResponse.json({ ...track, ...body });
	}),
	http.delete("*/api/media/music-tracks/:id", () => new HttpResponse(null, { status: 204 })),

	http.post("*/api/media/estimate", async ({ request }) => {
		const body = (await request.json()) as EstimateBody;
		const model =
			aiModelsMock.find((candidate) => candidate.id === body.aiModelId) ??
			aiModelsMock.find((candidate) => candidate.useCase === body.useCase && candidate.isDefault);

		if (!model) {
			return HttpResponse.json(
				{ code: "VALIDATION", message: "No model available for this use case" },
				{ status: 404 }
			);
		}

		const quantity = quantityFor(model.unit, body.quantities);
		const amountUsd = Math.round(quantity * model.unitPriceUsd * 100000) / 100000;

		return HttpResponse.json({
			amountUsd,
			currency: model.currency,
			aiModelId: model.id,
			modelDisplayName: model.displayName,
			pricesVerified: model.pricesVerified,
			breakdown: [
				{
					label: model.displayName,
					quantity,
					unit: model.unit,
					unitPriceUsd: model.unitPriceUsd,
					amountUsd,
				},
			],
		});
	}),

	http.post("*/api/media/jobs/phase-image", () =>
		HttpResponse.json({ job: mediaJobsMock[0] }, { status: 202 })
	),
	http.get("*/api/media/jobs/:id", ({ params }) => {
		const job = mediaJobsMock.find((candidate) => candidate.id === Number(params.id));

		return job
			? HttpResponse.json(job)
			: HttpResponse.json({ message: "Not found" }, { status: 404 });
	}),
	http.get("*/api/media/jobs", ({ request }) => {
		const courseId = new URL(request.url).searchParams.get("courseId");
		const items = mediaJobsMock.filter((job) => !courseId || job.courseId === Number(courseId));

		return HttpResponse.json({ items, total: items.length });
	}),
	http.post("*/api/media/jobs/:id/cancel", ({ params }) =>
		HttpResponse.json({
			...mediaJobsMock[0],
			id: Number(params.id),
			status: "CANCELLED",
		})
	),
	http.post("*/api/media/jobs/:id/select-asset", () =>
		HttpResponse.json(
			{
				id: 887,
				fullFilePath: mediaJobsMock[0].assets[0].fullFilePath,
				fileName: "job-4211-1-0.png",
				fileLength: 204800,
				fileType: "IMAGE",
				hash: "abc123",
				fileAssignment: "Content",
			},
			{ status: 201 }
		)
	),

	http.get("*/api/media/costs/company", () => HttpResponse.json(companyCostsMock)),

	/** The library, filtered the way the server filters it. */
	http.get("*/api/media/library", ({ request }) => {
		const courseId = new URL(request.url).searchParams.get("courseId");

		return HttpResponse.json(
			courseId
				? mediaLibraryMock.filter((asset) => asset.sourceCourseId === Number(courseId))
				: mediaLibraryMock
		);
	}),
	http.post("*/api/media/library/upload", () =>
		HttpResponse.json(
			{ id: 950, fullFilePath: "https://cdn.example.com/media/uploaded-briefing.mp4" },
			{ status: 201 }
		)
	),
	http.post("*/api/media/library/uploads/:id/assign", () =>
		HttpResponse.json({ id: 8802, fullFilePath: mediaLibraryMock[0].fullFilePath }, { status: 201 })
	),
	http.post("*/api/media/library/assets/:id/assign", () =>
		HttpResponse.json({ id: 8801, fullFilePath: mediaLibraryMock[0].fullFilePath }, { status: 201 })
	),
	http.delete("*/api/media/library/assets/:id", () => new HttpResponse(null, { status: 204 })),
	http.delete(
		"*/api/media/library/course-files/:id",
		() => new HttpResponse(null, { status: 204 })
	),
	http.get("*/api/media/costs/scenario/:courseId", () => HttpResponse.json(scenarioCostsMock)),
	http.get("*/api/media/costs/wizard/:sessionId", () => HttpResponse.json(videoWizardMock.costs)),

	/**
	 * The wizard fixture is deliberately static: every mutation answers with the same
	 * aggregate, which is enough to develop the rendering against. The state machine is
	 * the server's, and mocking it here would only reproduce it badly.
	 */
	http.post("*/api/course/:courseId/video-wizard", ({ params }) =>
		HttpResponse.json({ ...videoWizardMock, courseId: Number(params.courseId) }, { status: 201 })
	),
	http.get("*/api/course/:courseId/video-wizard", ({ params }) =>
		HttpResponse.json({ ...videoWizardMock, courseId: Number(params.courseId) })
	),
	http.get("*/api/media/video-wizard/:id", () => HttpResponse.json(videoWizardMock)),
	http.patch("*/api/media/video-wizard/:id", () => HttpResponse.json(videoWizardMock)),
	http.post("*/api/media/video-wizard/:id/storyboard/generate", () =>
		HttpResponse.json(videoWizardMock, { status: 202 })
	),
	http.post("*/api/media/video-wizard/:id/storyboard/versions/:versionId/activate", () =>
		HttpResponse.json(videoWizardMock)
	),
	http.post("*/api/media/video-wizard/:id/scenes", () => HttpResponse.json(videoWizardMock)),
	http.patch("*/api/media/video-wizard/:id/scenes/:sceneId", () =>
		HttpResponse.json(videoWizardMock)
	),
	http.delete("*/api/media/video-wizard/:id/scenes/:sceneId", () =>
		HttpResponse.json(videoWizardMock)
	),
	http.post("*/api/media/video-wizard/:id/scene-images/generate", () =>
		HttpResponse.json(videoWizardMock, { status: 202 })
	),
	http.post("*/api/media/video-wizard/:id/narration/preview", () =>
		HttpResponse.json(videoWizardMock, { status: 202 })
	),
	http.post("*/api/media/video-wizard/:id/scene-videos/generate", () =>
		HttpResponse.json(videoWizardMock, { status: 202 })
	),

	/**
	 * The one wizard mutation whose result is worth faking: approving a scene is what the
	 * pane is *for*, and a fixture that answered with the same aggregate would make the
	 * button look broken. The state machine itself still belongs to the server.
	 */
	http.post("*/api/media/video-wizard/:id/scenes/:sceneId/approve-video", ({ params }) =>
		HttpResponse.json({
			...videoWizardMock,
			storyboard: {
				...videoWizardMock.storyboard,
				scenes: videoWizardMock.storyboard.scenes.map((scene) =>
					scene.id === Number(params.sceneId) && scene.video
						? { ...scene, video: { ...scene.video, state: SceneArtifactState.APPROVED } }
						: scene
				),
			},
		})
	),

	/**
	 * The cut answers with the timeline the client just sent, so dragging a clip and
	 * trimming it look like they did something. Everything else about the aggregate — the
	 * gating, the costs, the invariants — is still the server's.
	 */
	http.put("*/api/media/video-wizard/:id/timeline", async ({ request }) => {
		const body = (await request.json()) as { clips: TimelineClipBody[] };

		return HttpResponse.json({
			...videoWizardMock,
			version: videoWizardMock.version + 1,
			timeline: (body.clips ?? []).map((clip, position) => ({ ...clip, position })),
		});
	}),

	/**
	 * The export answers 202 with the render in flight; a second call hands back the
	 * finished session. Worth faking, because the difference between "assembling" and
	 * "here is your video" is what the whole pane is for.
	 */
	http.post("*/api/media/video-wizard/:id/export", () => {
		exportCalls += 1;

		return exportCalls > 1
			? HttpResponse.json(exportedWizardMock, { status: 202 })
			: HttpResponse.json(
					{ ...videoWizardMock, activeJobs: [videoExportJobMock] },
					{ status: 202 }
				);
	}),

	/**
	 * Step 3's approval answers with the step still in progress and a narration job in
	 * flight — the one place the fixture has to differ from the static aggregate, because
	 * "approved" is not what that call means here.
	 */
	http.post("*/api/media/video-wizard/:id/steps/:step/approve", ({ params }) =>
		Number(params.step) === 3
			? HttpResponse.json({ ...videoWizardMock, activeJobs: [narrationJobMock] }, { status: 202 })
			: HttpResponse.json(videoWizardMock)
	),
];
